export type CloudinaryOpts = {
	width?: number;
	height?: number;
	quality?: number | "auto";
	crop?: "fill" | "fit";
	trim?: boolean | number;
};

export type CloudinaryVideoOpts = {
	width?: number;
	height?: number;
	quality?: number | "auto";
};

export function createCloudinaryUrls(cloudName: string) {
	const CLOUDINARY_IMAGE_UPLOAD_BASE = `https://res.cloudinary.com/${cloudName}/image/upload/`;
	const cloudinaryUrl = (publicId: string, opts?: CloudinaryOpts): string => {
		const transforms = ["f_auto", `q_${opts?.quality ?? "auto"}`];
		if (opts?.trim)
			transforms.push(typeof opts.trim === "number" ? `e_trim:${opts.trim}` : "e_trim");
		if (opts?.width) transforms.push(`w_${opts.width}`);
		if (opts?.height && opts?.crop) transforms.push(`h_${opts.height}`);
		if (opts?.crop) transforms.push(`c_${opts.crop}`);
		return `${CLOUDINARY_IMAGE_UPLOAD_BASE}${transforms.join(",")}/${publicId}`;
	};
	const cloudinaryVideoUrl = (publicId: string, opts?: CloudinaryVideoOpts): string => {
		const transforms = ["f_auto", `q_${opts?.quality ?? "auto"}`];
		if (opts?.width) transforms.push(`w_${opts.width}`);
		if (opts?.height) transforms.push(`h_${opts.height}`);
		return `https://res.cloudinary.com/${cloudName}/video/upload/${transforms.join(",")}/${publicId}`;
	};
	const cloudinaryVideoPoster = (publicId: string, opts?: CloudinaryOpts): string => {
		const transforms = ["f_auto", `q_${opts?.quality ?? "auto"}`];
		if (opts?.width) transforms.push(`w_${opts.width}`);
		if (opts?.height && opts?.crop) transforms.push(`h_${opts.height}`);
		if (opts?.crop) transforms.push(`c_${opts.crop}`);
		return `https://res.cloudinary.com/${cloudName}/video/upload/${transforms.join(",")}/so_0/${publicId}.jpg`;
	};
	const cloudinarySrcset = (
		publicId: string,
		baseWidth: number,
		opts?: Omit<CloudinaryOpts, "width">
	): string => {
		const widths = [baseWidth, Math.round(baseWidth * 1.5), baseWidth * 2];
		return widths
			.map((w) => `${cloudinaryUrl(publicId, { ...opts, width: w })} ${w}w`)
			.join(", ");
	};
	const cloudinaryFetch = (url: string | null | undefined, opts?: CloudinaryOpts): string => {
		if (!url) return "";
		if (url.includes("res.cloudinary.com")) return url;
		const transforms = ["f_auto", `q_${opts?.quality ?? "auto"}`];
		if (opts?.width) transforms.push(`w_${opts.width}`);
		if (opts?.height && opts?.crop) transforms.push(`h_${opts.height}`);
		if (opts?.crop) transforms.push(`c_${opts.crop}`);
		return `https://res.cloudinary.com/${cloudName}/image/fetch/${transforms.join(",")}/${encodeURIComponent(url)}`;
	};

	return {
		CLOUDINARY_IMAGE_UPLOAD_BASE,
		cloudinaryUrl,
		cloudinaryVideoUrl,
		cloudinaryVideoPoster,
		cloudinarySrcset,
		cloudinaryFetch
	};
}
